import type { EntitySet } from "./types.ts";

type EntityDisplayNameCapitalization = "title" | "lowercase";
type EntityDisplayNameForm = "singular" | "plural";

const entityDisplayNames: Record<
    EntitySet,
    Record<EntityDisplayNameCapitalization, Record<EntityDisplayNameForm, string>>
> = {
    siteCollections: {
        title: { singular: "Site Collection", plural: "Site Collections" },
        lowercase: { singular: "site collection", plural: "site collections" }
    },
    workspaces: {
        title: { singular: "Workspace", plural: "Workspaces" },
        lowercase: { singular: "workspace", plural: "workspaces" }
    },
    users: {
        title: { singular: "User", plural: "Users" },
        lowercase: { singular: "user", plural: "users" }
    },
    groups: {
        title: { singular: "Group", plural: "Groups" },
        lowercase: { singular: "group", plural: "groups" }
    },
    groupPrincipals: {
        title: { singular: "Group", plural: "Groups" },
        lowercase: { singular: "group", plural: "groups" }
    },
    licenses: {
        title: { singular: "License", plural: "Licenses" },
        lowercase: { singular: "license", plural: "licenses" }
    },
    sensitivityLabels: {
        title: { singular: "Sensitivity Label", plural: "Sensitivity Labels" },
        lowercase: { singular: "sensitivity label", plural: "sensitivity labels" }
    },
    sharedLinks: {
        title: { singular: "Sharing Link", plural: "Sharing Links" },
        lowercase: { singular: "sharing link", plural: "sharing links" }
    },
    securityGroups: {
        title: { singular: "Security Group", plural: "Security Groups" },
        lowercase: { singular: "security group", plural: "security groups" }
    },
    brokenInheritanceEntities: {
        title: { singular: "Broken Inheritance Object", plural: "Broken Inheritance Objects" },
        lowercase: { singular: "broken inheritance object", plural: "broken inheritance objects" }
    },
    userLicenses: {
        title: { singular: "User License", plural: "User Licenses" },
        lowercase: { singular: "user license", plural: "user licenses" }
    },
    oneDrives: {
        title: { singular: "OneDrive", plural: "OneDrives" },
        lowercase: { singular: "OneDrive", plural: "OneDrives" }
    },
    archivedSiteCollections: {
        title: { singular: "Archived Site", plural: "Archived Sites" },
        lowercase: { singular: "archived site", plural: "archived sites" }
    },
    activityLogs: {
        title: { singular: "Activity Log", plural: "Activity Logs" },
        lowercase: { singular: "activity log", plural: "activity logs" }
    },
    sharedLinkInvitations: {
        title: { singular: "Sharing Link Invitation", plural: "Sharing Link Invitations" },
        lowercase: { singular: "sharing link invitation", plural: "sharing link invitations" }
    },
    customReports: {
        title: { singular: "Custom Report", plural: "Custom Reports" },
        lowercase: { singular: "custom report", plural: "custom reports" }
    },
    reportShares: {
        title: { singular: "Shared Report", plural: "Shared Reports" },
        lowercase: { singular: "shared report", plural: "shared reports" }
    },
    reportShareRecipients: {
        title: { singular: "Recipient", plural: "Recipients" },
        lowercase: { singular: "recipient", plural: "recipients" }
    },
    automations: {
        title: { singular: "Automation", plural: "Automations" },
        lowercase: { singular: "automation", plural: "automations" }
    },
    reviews: {
        title: { singular: "Review", plural: "Reviews" },
        lowercase: { singular: "review", plural: "reviews" }
    },
    reviewItems: {
        title: { singular: "Review Item", plural: "Review Items" },
        lowercase: { singular: "review item", plural: "review items" }
    },
    dynamicGroups: {
        title: { singular: "Dynamic Group", plural: "Dynamic Groups" },
        lowercase: { singular: "dynamic group", plural: "dynamic groups" }
    },
    reviewTasks: {
        title: { singular: "Review Task", plural: "Review Tasks" },
        lowercase: { singular: "review task", plural: "review tasks" }
    },
    reviewAnswers: {
        title: { singular: "Review Answer", plural: "Review Answers" },
        lowercase: { singular: "review answer", plural: "review answers" }
    },
    reviewAnswerSharedLinks: {
        title: { singular: "Sharing Link", plural: "Sharing Links" },
        lowercase: { singular: "sharing link", plural: "sharing links" }
    },
    reviewAnswerSharedLinkRecipients: {
        title: { singular: "Sharing Link Recipient", plural: "Sharing Link Recipients" },
        lowercase: { singular: "sharing link recipient", plural: "sharing link recipients" }
    },
    reviewAnswerMembership: {
        title: { singular: "Member", plural: "Members" },
        lowercase: { singular: "member", plural: "members" }
    },
    reviewAnswerFiles: {
        title: { singular: "File", plural: "Files" },
        lowercase: { singular: "file", plural: "files" }
    },
    doNotDisturbUsers: {
        title: { singular: "Do Not Disturb User", plural: "Do Not Disturb Users" },
        lowercase: { singular: "do not disturb user", plural: "do not disturb users" }
    },
    doNotDisturbWorkspaces: {
        title: { singular: "Do Not Disturb Workspace", plural: "Do Not Disturb Workspaces" },
        lowercase: { singular: "do not disturb workspace", plural: "do not disturb workspaces" }
    },
    reviewNotificationRecipients: {
        title: { singular: "User", plural: "Users" },
        lowercase: { singular: "user", plural: "users" }
    },
    reviewSelectedWorkspaces: {
        title: { singular: "Workspace", plural: "Workspaces" },
        lowercase: { singular: "workspace", plural: "workspaces" }
    },
    reviewReviewers: {
        title: { singular: "Reviewer", plural: "Reviewers" },
        lowercase: { singular: "reviewer", plural: "reviewers" }
    },
    reviewUsers: {
        title: { singular: "User", plural: "Users" },
        lowercase: { singular: "user", plural: "users" }
    },
    tenantSettings: {
        title: { singular: "Tenant Setting", plural: "Tenant Settings" },
        lowercase: { singular: "tenant setting", plural: "tenant settings" }
    },
    documentLibraries: {
        title: { singular: "Library", plural: "Libraries" },
        lowercase: { singular: "library", plural: "libraries" }
    },
    bulkOperations: {
        title: { singular: "Bulk Operation", plural: "Bulk Operations" },
        lowercase: { singular: "bulk operation", plural: "bulk operations" }
    },
    workspaceReviews: {
        title: { singular: "Workspace Review", plural: "Workspace Reviews" },
        lowercase: { singular: "workspace review", plural: "workspace reviews" }
    }
};

interface EntityDisplayNameFormatOptions {
    form?: EntityDisplayNameForm;
    capitalization?: EntityDisplayNameCapitalization;
}

// If possible, use the values returned from the Metadata API instead of this function.
export function formatEntityDisplayName(entitySet: EntitySet, options?: EntityDisplayNameFormatOptions): string {
    const { form = "singular", capitalization = "title" } = options ?? {};

    return entityDisplayNames[entitySet][capitalization][form];
}
